import useAxiosInstance from '../hooks/useAxiosInstance';
import { createContext, useContext, useEffect, useState } from 'react';

// excluded paths
const excludedCalls = Object.freeze([
    process.env.REACT_APP_SUBMIT_ORDER_ORIGIN_URL,
    process.env.REACT_APP_DETECT_DOCUMENT_ENDPOINT,
    process.env.REACT_APP_VERIFY_DOCUMENT_ENDPOINT,
]);

function useInitAxiosLoaderContext() {
  const axios = useAxiosInstance();
  const [numberOfCalls, setNumberOfCalls] = useState(0);
  const [submitOrderCalls, setSubmitOrderCalls] = useState(0);

  useEffect(() => {
    let _numberOfCalls = 0;
    let _submitOrderCalls = 0;
    const requestInterceptor = axios.interceptors().request.use(
      (config) => {
        if (!excludedCalls.includes(config.url)) {
          setNumberOfCalls(++_numberOfCalls);
        } else {
          setSubmitOrderCalls(++_submitOrderCalls);
        }
        return config;
      },
      (error) => {
        if (!excludedCalls.includes(error.config.url)) {
          setNumberOfCalls(--_numberOfCalls);
        } else {
          setSubmitOrderCalls(--_submitOrderCalls);
        }
        return Promise.reject(error);
      }
    );
    const responseInterceptor = axios.interceptors().response.use(
      (response) => {
        if (!excludedCalls.includes(response.config.url)) {
          setNumberOfCalls(--_numberOfCalls);
        } else {
          setSubmitOrderCalls(--_submitOrderCalls);
        }
        return response;
      },
      (error) => {
        if (!excludedCalls.includes(error.config.url)) {
          setNumberOfCalls(--_numberOfCalls);
        } else {
          setSubmitOrderCalls(--_submitOrderCalls);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors().request.eject(requestInterceptor);
      axios.interceptors().response.eject(responseInterceptor);
    };
  });

  return { loading: numberOfCalls > 0, numberOfCalls: numberOfCalls, submitLoading: submitOrderCalls > 0, submitOrderCalls: submitOrderCalls };
}

const AxiosLoaderContext = createContext(null);

/**
 * @return {{loading: boolean, numberOfCalls: number}}
 */
export function useAxiosLoaderContext() {
  const value = useContext(AxiosLoaderContext);
  if (value == null) throw new Error('AxiosLoaderContext is not defined above current component.');
  return {
    loading: value.loading,
    numberOfCalls: value.numberOfCalls,
    submitLoading: value.submitLoading,
    submitOrderCalls: value.submitOrderCalls
  };
}

export function AxiosLoaderContextProvider({ children }) {
  const value = useInitAxiosLoaderContext();
  return <AxiosLoaderContext.Provider value={value}>{children}</AxiosLoaderContext.Provider>;
}
